import { call, put, takeLatest } from 'redux-saga/effects';
import api from '../../../utils/api';
import config from '../../../config';
import i18n from '../../../i18n';
import positionService from '../../../services/positionService';
import {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchPositions,
  fetchPositionsSuccess,
  fetchPositionsFailure,
  fetchPositionById,
  fetchPositionByIdSuccess,
  fetchPositionByIdFailure,
  addPosition,
  addPositionSuccess,
  addPositionFailure,
  updatePosition,
  updatePositionSuccess,
  updatePositionFailure,
  deletePosition,
  deletePositionSuccess,
  deletePositionFailure,
  bulkDeletePositions,
  bulkDeletePositionsSuccess,
  bulkDeletePositionsFailure,
} from './slice';

/**
 * Translate error message from backend
 * Maps common backend error messages to i18n translation keys
 */
function translateError(errorMessage) {
  if (!errorMessage) return i18n.t('positions.errors.unknown');

  // Common error message mappings
  const errorMap = {
    'Position name already exists': 'positions.errors.nameExists',
    'Position not found': 'positions.errors.positionNotFound',
    'Failed to add position': 'positions.errors.addFailed',
    'Failed to update position': 'positions.errors.updateFailed',
    'Failed to delete position': 'positions.errors.deleteFailed',
    'Failed to fetch positions': 'positions.errors.fetchFailed',
    'Failed to fetch init data': 'positions.errors.initFailed',
  };

  // Check if error message matches a known pattern
  for (const [key, translationKey] of Object.entries(errorMap)) {
    if (errorMessage.includes(key)) {
      return i18n.t(translationKey);
    }
  }

  // If no mapping found, return original message
  return errorMessage;
}

function* handleFetchInit() {
  try {
    const response = yield call(positionService.getInit);
    yield put(fetchInitSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchInitFailure(translatedError));
  }
}

function* handleFetchPositions(action) {
  try {
    const response = yield call(positionService.getPositions, action.payload);
    yield put(fetchPositionsSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchPositionsFailure(translatedError));
  }
}

function* handleFetchPositionById(action) {
  try {
    const response = yield call(positionService.getPositionById, action.payload);
    yield put(fetchPositionByIdSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchPositionByIdFailure(translatedError));
  }
}

function* handleAddPosition(action) {
  try {
    const response = yield call(positionService.createPosition, action.payload);
    yield put(addPositionSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(addPositionFailure(translatedError));
  }
}

function* handleUpdatePosition(action) {
  try {
    const response = yield call(positionService.updatePosition, action.payload);
    yield put(updatePositionSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(updatePositionFailure(translatedError));
  }
}

function* handleDeletePosition(action) {
  try {
    const positionId = action.payload;
    yield call(api.delete, `${config.endpoints.positions.delete}/${positionId}`);
    yield put(deletePositionSuccess(positionId));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(deletePositionFailure(translatedError));
  }
}

// Worker saga: Bulk delete positions
function* handleBulkDeletePositions(action) {
  try {
    const positionIds = action.payload;
    // Call API DELETE /positions?ids=1&ids=2&ids=3
    // Use custom paramsSerializer to format array as repeated params
    yield call(api.delete, config.endpoints.positions.delete, {
      params: { ids: positionIds },
      paramsSerializer: {
        indexes: null, // Use repeat format: ?ids=1&ids=2 instead of ?ids[0]=1&ids[1]=2
      },
    });
    yield put(bulkDeletePositionsSuccess(positionIds));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(bulkDeletePositionsFailure(translatedError));
  }
}

export function* watchPositionsActions() {
  yield takeLatest(fetchInit.type, handleFetchInit);
  yield takeLatest(fetchPositions.type, handleFetchPositions);
  yield takeLatest(fetchPositionById.type, handleFetchPositionById);
  yield takeLatest(addPosition.type, handleAddPosition);
  yield takeLatest(updatePosition.type, handleUpdatePosition);
  yield takeLatest(deletePosition.type, handleDeletePosition);
  yield takeLatest(bulkDeletePositions.type, handleBulkDeletePositions);
}
